import React from 'react';
import { Typography } from 'components/Typography';
import { useIsMobileDevice } from 'utils';
import * as Styled from './MBDialog.styles';
import { MBDialogProps } from './MBDialog.types';

const MBDialog: React.FC<MBDialogProps> = ({
  children,
  onClose,
  onApplyButtonClick = null,
  onCancelButtonClick = null,
  onGoBack,
  title,
  applyButtonLabel = 'Apply',
  cancelButtonLabel = 'Cancel',
  goBackLabel = 'Back',
  dialogWidth = 600,
  isApplyLoading = false,
  icon,
  showCloseIcon = false,
  paddingSize = 'medium',
  showActionTopBorder = false,
  leftButtonLabel = 'Create Board',
  onLeftButtonClick,
  titleBottomPadding,
  modalId = '',
  bottomChildren = null,
  isPalette = false,
  dialogPaletteUnitsSize = 1.5,
  actionChildren = null,
  mobileFullscreen = false,
  shouldDisableBottomPadding = false,
  leftButtonVariant = 'text',
  leftButtonDense = true,
  leftButtonColor,
  leftButtonSx,
  applyButtonSx,
  cancelButtonSx,
  applyButtonDisabled = false,
  dialogTitleSx,
  showBottomActions = true,
  fullScreenModal = false,
  doNotScrollContent = false,
  leftButtonProps,
  ...rest
}) => {
  const isSmallerThanTablet = useIsMobileDevice('md');
  const shouldShowGoBack = Boolean(onGoBack);

  return (
    <Styled.Dialog
      fullScreen={mobileFullscreen && isSmallerThanTablet}
      onClose={onClose}
      dialogWidth={dialogWidth}
      paddingSize={paddingSize}
      titleBottomPadding={titleBottomPadding}
      isPalette={isPalette}
      dialogPaletteUnitsSize={dialogPaletteUnitsSize}
      fullScreenModal={fullScreenModal}
      {...rest}
    >
      <Styled.Wrapper id={modalId}>
        {showCloseIcon && (
          <Styled.CloseIconButton
            id="close_modal_button"
            onClick={onClose}
            color="inherit"
            iconName="close"
          />
        )}
        {shouldShowGoBack && (
          <Styled.BackHeadline>
            <Styled.GoBackButton iconName="back" dense onClick={onGoBack} />
            <Styled.GoBackTitle variant="h3">{goBackLabel}</Styled.GoBackTitle>
          </Styled.BackHeadline>
        )}
        {icon && icon}
        {title && (
          <Styled.DialogTitle
            mb={1.5}
            isSmallMobileHeadline={!shouldShowGoBack}
          >
            <Typography variant="h3" fontWeight={600} sx={dialogTitleSx}>
              {title}
            </Typography>
          </Styled.DialogTitle>
        )}
        <Styled.DialogContent
          shouldDisableBottomPadding={shouldDisableBottomPadding}
          // @ts-expect-error // TODO: fix types
          doNotScrollContent={doNotScrollContent}
        >
          {children}
        </Styled.DialogContent>
        {showActionTopBorder && (
          <Styled.ActionBorder paddingSize={paddingSize} />
        )}
        {(onCancelButtonClick != null || onApplyButtonClick != null) &&
          showBottomActions && (
            <Styled.DialogActions>
              {actionChildren}
              {onLeftButtonClick != null && (
                <Styled.LeftButton
                  dense={leftButtonDense}
                  variant={leftButtonVariant}
                  startIcon="plus"
                  color={leftButtonColor}
                  onClick={onLeftButtonClick}
                  sx={leftButtonSx}
                  {...leftButtonProps}
                >
                  {leftButtonLabel}
                </Styled.LeftButton>
              )}
              {onCancelButtonClick != null && (
                <Styled.Button
                  onClick={onCancelButtonClick}
                  role="button"
                  size="large"
                  variant="contained"
                  color="secondary"
                  sx={cancelButtonSx}
                >
                  {cancelButtonLabel}
                </Styled.Button>
              )}
              {onApplyButtonClick != null && (
                <Styled.Button
                  disabled={applyButtonDisabled || isApplyLoading}
                  onClick={onApplyButtonClick}
                  role="button"
                  size="large"
                  variant="contained"
                  sx={applyButtonSx}
                >
                  {applyButtonLabel}
                </Styled.Button>
              )}
            </Styled.DialogActions>
          )}
        {bottomChildren != null && (
          <Styled.DialogContent>{bottomChildren}</Styled.DialogContent>
        )}
      </Styled.Wrapper>
    </Styled.Dialog>
  );
};

export default MBDialog;
